import { type ChangeEvent, useCallback } from 'react';
import type { Lens } from '@hookform/lenses';
import { useFormContext, useWatch } from 'react-hook-form';
import { match, P } from 'ts-pattern';
import { z } from 'zod/v4';
import { IsoV1Country, type IxcRegistrationV1Address } from '@interxfi/types/generated/api';
import { AppSelectInputWithLens } from '../../../../components/form/app-select-input';
import { buildCountryOptions } from '../../../../util/country';
import {
  DEFAULT_US_ADDRESS_FORM_LAYOUT,
  defaultUSAddressFormValues,
  formValuesToUSAddress,
  getInitialFormValuesForUSAddress,
  STACKED_US_ADDRESS_FORM_LAYOUT,
  USAddressForm,
  type USAddressFormLabels,
  type USAddressFormLayout,
  usAddressFormSchema,
} from './us-address-form';

const countryOptions = buildCountryOptions('en-US', [IsoV1Country.Us]);

export const addressFormSchema = z.discriminatedUnion('type', [usAddressFormSchema]);

export type AddressFormValues = z.infer<typeof addressFormSchema>;

export const defaultAddressFormValues: AddressFormValues = { ...defaultUSAddressFormValues };

export function formValuesToAddress(values: AddressFormValues): IxcRegistrationV1Address {
  return match(values)
    .with({ type: IsoV1Country.Us }, (usAddress) => ({
      usAddress: formValuesToUSAddress(usAddress),
    }))
    .exhaustive();
}

export function getInitialFormValuesForAddress(address: IxcRegistrationV1Address | undefined): AddressFormValues | undefined {
  return match(address)
    .with({ usAddress: P.not(P.nullish) }, ({ usAddress }) => getInitialFormValuesForUSAddress(usAddress))
    .otherwise(() => undefined);
}

const defaultValuesByCountry: Partial<Record<IsoV1Country, AddressFormValues>> = {
  [IsoV1Country.Us]: { ...defaultUSAddressFormValues },
} as const;

export interface AddressFormLabels {
  usAddress: USAddressFormLabels;
}

export interface AddressFormLayout {
  usAddress: USAddressFormLayout;
}

export const DEFAULT_ADDRESS_FORM_LAYOUT = {
  usAddress: DEFAULT_US_ADDRESS_FORM_LAYOUT,
} as const satisfies AddressFormLayout;

export const STACKED_ADDRESS_FORM_LAYOUT = {
  usAddress: STACKED_US_ADDRESS_FORM_LAYOUT,
} as const satisfies AddressFormLayout;

interface AddressFormProps {
  labels?: AddressFormLabels;
  layout?: AddressFormLayout;
  lens: Lens<AddressFormValues>;
  tooltip?: string;
}

export function AddressForm({ labels, layout = DEFAULT_ADDRESS_FORM_LAYOUT, lens, tooltip }: AddressFormProps) {
  const { setValue } = useFormContext();
  const { name } = lens.interop();
  const typeLens = lens.focus('type');
  const country = useWatch({ ...typeLens.interop(), defaultValue: IsoV1Country.Us }) as unknown as IsoV1Country;
  const handleCountryChange = useCallback(
    (e: ChangeEvent<HTMLSelectElement>) => {
      const valuesForSelection = defaultValuesByCountry[(e.target.value || IsoV1Country.Us) as IsoV1Country];
      if (!valuesForSelection) {
        return;
      }

      setValue(name, { ...valuesForSelection });
    },
    [setValue, name],
  );

  const countrySelectInput = (
    <AppSelectInputWithLens label="Country" lens={typeLens} onChange={handleCountryChange} options={countryOptions} required />
  );

  switch (country) {
    default:
      return <USAddressForm countryInput={countrySelectInput} labels={labels?.usAddress} layout={layout.usAddress} lens={lens} tooltip={tooltip} />;
  }
}
